import { z } from 'zod';
import { registry } from '../registry';
const wnlMessage = { message: 'Mark as within normal limits if none applies', path: [] };
export const neurosensoryCardTitles = {
    mentalStatus: 'Mental Status',
    cognitiveBehavioral: 'Cognitive / Behavioral Symptoms',
    neurologicalFindings: 'Neurological Findings',
    handGrips: 'Hand Grips',
    extremities: 'Extremities',
    comprehensionLanguage: 'Comprehension & Language',
    pupils: 'Pupils',
    vision: 'Vision',
    hearing: 'Hearing',
    entMouth: 'ENT Mouth',
    psychosocial: 'Psychosocial',
    painAssessment: 'Pain Profile',
};
const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
const extremityOptions = [
    { value: 'Weakness', label: 'Weakness' },
    { value: 'Paresis', label: 'Paresis' },
    { value: 'Spasticity', label: 'Spasticity' },
    { value: 'Contracture', label: 'Contracture' },
];
const pupilReactivityOptions = [
    { value: 'reactive', label: 'Reactive' },
    { value: 'non-reactive', label: 'Non-reactive' },
    { value: 'sluggish', label: 'Sluggish' },
];
const earAbilityOptions = [
    { value: 'adequate', label: 'Adequate' },
    { value: 'impaired', label: 'Impaired' },
    { value: 'deaf', label: 'Deaf' },
];
const eyeImpairmentOptions = [
    { value: 'no-impairment', label: 'No impairment' },
    { value: 'partial', label: 'Partial impairment' },
    { value: 'severe', label: 'Severe impairment' },
];
export const baseNeuroMentalStatus = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    orientedToPerson: z
        .string()
        .optional()
        .register(registry, { label: 'Person', options: [...yesNoOptions] }),
    orientedToPlace: z
        .string()
        .optional()
        .register(registry, { label: 'Place', options: [...yesNoOptions] }),
    orientedToPurpose: z
        .string()
        .optional()
        .register(registry, { label: 'Purpose', options: [...yesNoOptions] }),
    orientedToTime: z
        .string()
        .optional()
        .register(registry, { label: 'Time', options: [...yesNoOptions] }),
})
    .register(registry, { label: neurosensoryCardTitles.mentalStatus });
export const baseCognitiveBehavioral = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    confused: z.boolean().optional().register(registry, { label: 'Confused' }),
    lethargic: z.boolean().optional().register(registry, { label: 'Lethargic' }),
    forgetful: z.boolean().optional().register(registry, { label: 'Forgetful' }),
    aphasiac: z.boolean().optional().register(registry, { label: 'Aphasiac' }),
    aphasiaDetails: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Aphasia details',
        options: [
            { value: 'expressive', label: "Expressive (Broca's)" },
            { value: 'receptive', label: "Receptive (Wernicke's)" },
            { value: 'global', label: 'Global' },
            { value: 'dysarthriaOnly', label: 'Dysarthria Only' },
            { value: 'augmentativeDevice', label: 'Augmentative Device in Use' },
        ],
    }),
    dizziness: z.boolean().optional().register(registry, { label: 'Dizziness' }),
    headache: z.boolean().optional().register(registry, { label: 'Headache' }),
    lightheaded: z.boolean().optional().register(registry, { label: 'Lightheaded' }),
})
    .register(registry, { label: neurosensoryCardTitles.cognitiveBehavioral });
export const baseNeurologicalFindings = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    details: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Findings',
        options: [
            { value: 'tremors', label: 'Tremors' },
            { value: 'numbnessTingling', label: 'Numbness / Tingling' },
            { value: 'facialDroop', label: 'Facial Droop / Asymmetry' },
            { value: 'ataxia', label: 'Ataxia / Coordination Impaired' },
            { value: 'seizureActivity', label: 'Seizure Activity This Visit' },
            { value: 'syncope', label: 'Syncope / Near-Syncope' },
        ],
    }),
    neuroImpactsADLs: z.boolean().optional().register(registry, { label: 'Neuro finding impacts ADLs — addressed in POC' }),
})
    .register(registry, { label: neurosensoryCardTitles.neurologicalFindings });
export const baseHandGrips = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    dominantHand: z
        .string()
        .optional()
        .register(registry, {
        label: 'Dominant Hand',
        options: [
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' },
        ],
    }),
    equality: z
        .string()
        .optional()
        .register(registry, {
        label: 'Equality',
        options: [
            { value: 'equal', label: 'Equal' },
            { value: 'unequal', label: 'Unequal' },
        ],
    }),
    leftStrength: z
        .string()
        .optional()
        .register(registry, {
        label: 'Left',
        options: [
            { value: 'strong', label: 'Strong' },
            { value: 'weak', label: 'Weak' },
        ],
    }),
    rightStrength: z
        .string()
        .optional()
        .register(registry, {
        label: 'Right',
        options: [
            { value: 'strong', label: 'Strong' },
            { value: 'weak', label: 'Weak' },
        ],
    }),
})
    .register(registry, { label: neurosensoryCardTitles.handGrips });
export const baseExtremities = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    lue: z
        .string()
        .optional()
        .register(registry, { label: 'LUE', options: [...extremityOptions] }),
    rue: z
        .string()
        .optional()
        .register(registry, { label: 'RUE', options: [...extremityOptions] }),
    lle: z
        .string()
        .optional()
        .register(registry, { label: 'LLE', options: [...extremityOptions] }),
    rle: z
        .string()
        .optional()
        .register(registry, { label: 'RLE', options: [...extremityOptions] }),
})
    .register(registry, { label: neurosensoryCardTitles.extremities });
export const baseComprehensionLanguage = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    details: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Literacy',
        options: [
            { value: 'readsWritesFluently', label: 'Reads / Writes Fluently' },
            { value: 'limitedLiteracy', label: 'Limited Literacy' },
            { value: 'nonLiterate', label: 'Non-Literate' },
            { value: 'visualCognitiveBarrier', label: 'Visual / Cognitive Barrier' },
        ],
    }),
})
    .register(registry, { label: neurosensoryCardTitles.comprehensionLanguage });
export const basePupils = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    equality: z
        .string()
        .optional()
        .register(registry, {
        label: 'Equality',
        options: [
            { value: 'equal', label: 'Equal' },
            { value: 'unequal-anisocoria', label: 'Unequal / Anisocoria' },
        ],
    }),
    shape: z
        .string()
        .optional()
        .register(registry, {
        label: 'Shape',
        options: [
            { value: 'round', label: 'Round' },
            { value: 'irregular', label: 'Irregular' },
        ],
    }),
    size: z
        .string()
        .optional()
        .register(registry, {
        label: 'Size',
        options: [
            { value: 'normal', label: 'Normal' },
            { value: 'constricted', label: 'Constricted / Pinpoint' },
            { value: 'dilated', label: 'Dilated' },
        ],
    }),
    leftReactivity: z
        .string()
        .optional()
        .register(registry, { label: 'Left Pupil', options: [...pupilReactivityOptions] }),
    rightReactivity: z
        .string()
        .optional()
        .register(registry, { label: 'Right Pupil', options: [...pupilReactivityOptions] }),
})
    .register(registry, { label: neurosensoryCardTitles.pupils });
export const baseEntMouth = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    details: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Findings',
        options: [
            { value: 'congestion', label: 'Congestion' },
            { value: 'dysphagia', label: 'Dysphagia' },
            { value: 'gingivitis', label: 'Gingivitis' },
            { value: 'epistaxis', label: 'Epistaxis' },
        ],
    }),
})
    .register(registry, { label: neurosensoryCardTitles.entMouth });
export const baseVisionSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    correctiveLenses: z
        .string()
        .optional()
        .register(registry, {
        label: 'Corrective lenses in use',
        options: [
            { value: 'glasses', label: 'Glasses' },
            { value: 'contacts', label: 'Contacts' },
            { value: 'none', label: 'None' },
        ],
    }),
    visionSeverity: z
        .string()
        .optional()
        .register(registry, {
        label: 'Visual impairment severity',
        options: [
            { value: 'adequate', label: 'Adequate' },
            { value: 'impaired', label: 'Impaired — large print only' },
            { value: 'moderately-impaired', label: 'Moderately impaired' },
            { value: 'highly-impaired', label: 'Highly impaired' },
            { value: 'severely-impaired', label: 'Severely impaired / No vision' },
        ],
    }),
    leftEye: z
        .string()
        .optional()
        .register(registry, { label: 'Left Eye', options: [...eyeImpairmentOptions] }),
    rightEye: z
        .string()
        .optional()
        .register(registry, { label: 'Right Eye', options: [...eyeImpairmentOptions] }),
    visualFieldDefect: z
        .string()
        .optional()
        .register(registry, {
        label: 'Visual field defect (if CVA/TIA dx)',
        options: [
            { value: 'hemianopia', label: 'Hemianopia' },
            { value: 'quadrant-defect', label: 'Quadrant defect' },
            { value: 'none-noted', label: 'None noted' },
        ],
    }),
    impactOnCare: z
        .array(z.string())
        .default([])
        .register(registry, {
        label: 'Impact on care delivery',
        options: [
            { value: 'affects-medication-reading', label: 'Affects medication reading' },
            { value: 'safety-fall-navigation', label: 'Safety risk — fall / navigation' },
            { value: 'affects-education', label: 'Affects education materials' },
        ],
    }),
})
    .register(registry, { label: neurosensoryCardTitles.vision });
export const baseHearingSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    hearingAidStatus: z
        .string()
        .optional()
        .register(registry, {
        label: 'Hearing aid in use',
        options: [
            { value: 'present-effective', label: 'Hearing aid present and effective' },
            { value: 'present-not-worn', label: 'Present — not worn this visit' },
            { value: 'present-malfunction', label: 'Present — malfunction noted' },
            { value: 'none', label: 'No hearing aid' },
        ],
    }),
    hearingAbility: z
        .string()
        .optional()
        .register(registry, {
        label: 'Hearing ability',
        options: [
            { value: 'adequate', label: 'Adequate' },
            { value: 'minimal-difficulty', label: 'Minimal difficulty' },
            { value: 'moderate-difficulty', label: 'Moderate difficulty' },
            { value: 'highly-impaired', label: 'Highly impaired / No useful hearing' },
        ],
    }),
    leftEar: z
        .string()
        .optional()
        .register(registry, { label: 'Left Ear', options: [...earAbilityOptions] }),
    rightEar: z
        .string()
        .optional()
        .register(registry, { label: 'Right Ear', options: [...earAbilityOptions] }),
    comprehension: z
        .string()
        .optional()
        .register(registry, {
        label: 'Comprehension — B0800',
        options: [
            { value: 'fully', label: 'Understands instructions fully' },
            { value: 'usually', label: 'Usually understands' },
            { value: 'sometimes', label: 'Sometimes understands' },
            { value: 'rarely-never', label: 'Rarely / never understands' },
        ],
    }),
    interpreterNeeded: z
        .string()
        .optional()
        .register(registry, {
        label: 'Interpreter needed — A1110 (OASIS-E2)',
        options: [
            { value: 'no', label: 'No interpreter needed' },
            { value: 'yes', label: 'Interpreter needed' },
            { value: 'unable-to-determine', label: 'Unable to determine' },
        ],
    }),
    impactOnCare: z
        .array(z.string())
        .default([])
        .register(registry, {
        label: 'Impact on care delivery',
        options: [
            { value: 'affects-education', label: 'Affects education delivery' },
            { value: 'safety-alarms', label: 'Safety concern — cannot hear alarms' },
            { value: 'adapted-technique', label: 'Adapted technique used this visit' },
        ],
    }),
})
    .register(registry, { label: neurosensoryCardTitles.hearing });
export const visionRefined = baseVisionSchema.refine((d) => d.wnl || d.correctiveLenses || d.visionSeverity || d.leftEye || d.rightEye || d.visualFieldDefect || !!d.impactOnCare?.length, wnlMessage);
export const hearingRefined = baseHearingSchema.refine((d) => d.wnl ||
    d.hearingAidStatus ||
    d.hearingAbility ||
    d.leftEar ||
    d.rightEar ||
    d.comprehension ||
    d.interpreterNeeded ||
    !!d.impactOnCare?.length, wnlMessage);
export const baseNeurologicalSchemaRefined = z.object({
    mentalStatus: baseNeuroMentalStatus.refine((d) => d.wnl || d.orientedToPerson || d.orientedToPlace || d.orientedToPurpose || d.orientedToTime, wnlMessage),
    cognitiveBehavioral: baseCognitiveBehavioral.refine((d) => d.wnl || d.confused || d.lethargic || d.forgetful || d.aphasiac || d.dizziness || d.headache || d.lightheaded, wnlMessage),
    neurologicalFindings: baseNeurologicalFindings.refine((d) => d.wnl || !!d.details?.length || d.neuroImpactsADLs, wnlMessage),
    handGrips: baseHandGrips.refine((d) => d.wnl || d.dominantHand || d.equality || d.leftStrength || d.rightStrength, wnlMessage),
    extremities: baseExtremities.refine((d) => d.wnl || d.lue || d.rue || d.lle || d.rle, wnlMessage),
    comprehensionLanguage: baseComprehensionLanguage.refine((d) => d.wnl || !!d.details?.length, wnlMessage),
    pupils: basePupils.refine((d) => d.wnl || d.equality || d.shape || d.size || d.leftReactivity || d.rightReactivity, wnlMessage),
    vision: visionRefined.optional(),
    hearing: hearingRefined.optional(),
    entMouth: baseEntMouth.refine((d) => d.wnl || !!d.details?.length, wnlMessage).optional(),
});
export const baseNeurologicalSchema = z.object({
    mentalStatus: baseNeuroMentalStatus,
    cognitiveBehavioral: baseCognitiveBehavioral,
    neurologicalFindings: baseNeurologicalFindings,
    handGrips: baseHandGrips,
    extremities: baseExtremities,
    comprehensionLanguage: baseComprehensionLanguage,
    pupils: basePupils.optional(),
    vision: visionRefined.optional(),
    hearing: hearingRefined.optional(),
    entMouth: baseEntMouth.refine((d) => d.wnl || !!d.details?.length, wnlMessage).optional(),
});
/** Separate mental-status shape used outside Neurosensory (e.g. 485-POC). */
export const baseMentalStatusSchema = z.object({
    wnl: z.boolean().optional(),
    alertness: z.string().optional(),
    orientedToPerson: z.string().optional(),
    orientedToPlace: z.string().optional(),
    orientedToPurpose: z.string().optional(),
    orientedToTime: z.string().optional(),
    status: z.array(z.string()).optional(),
});
